import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  ArrowLeft, Send, Bot, Loader2, X, Sparkles,
  MessageCircle, Clock
} from 'lucide-react';
import VoiceRecorder from '../../../components/VoiceRecorder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple Avatar Component for Trial
const TrialAvatar = ({ avatar, isSpeaking = false }) => (
  <div className={`w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center ${isSpeaking ? 'animate-pulse' : ''}`}>
    {avatar?.image_url ? (
      <img src={avatar.image_url} alt={avatar?.display_name} className="w-full h-full object-cover" />
    ) : (
      <Bot className="w-8 h-8 text-white" />
    )}
  </div>
);

// Message Component
const TrialMessage = ({ message, isUser }) => (
  <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
    <div className={`
      max-w-[80%] rounded-2xl px-4 py-3
      ${isUser 
        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' 
        : 'bg-slate-700/50 text-gray-200'
      }
    `}>
      <p className="whitespace-pre-wrap text-sm">{message.content}</p>
    </div>
  </div>
);

// Free Trial View Component
const FreeTrialView = ({
  onBack,
  avatars = [],
  trialStatus,
  setTrialStatus,
  onBuyPass
}) => {
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showVoiceInput, setShowVoiceInput] = useState(false);
  const [trialId, setTrialId] = useState(null);
  
  const messagesEndRef = useRef(null);
  
  useEffect(() => {
    // Get or create trial ID
    let storedTrialId = localStorage.getItem('sankat_mochan_trial_id');
    if (!storedTrialId) {
      storedTrialId = `trial_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      localStorage.setItem('sankat_mochan_trial_id', storedTrialId);
    }
    setTrialId(storedTrialId);
    fetchTrialStatus(storedTrialId);
  }, []);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const fetchTrialStatus = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/trial/status?trial_id=${id}`);
      const data = await res.json();
      setTrialStatus(data);
    } catch (error) {
      console.error('Error fetching trial status:', error);
    }
  };
  
  const sendTrialMessage = async (content) => {
    if (!content.trim()) return;
    if (!selectedAvatar) {
      toast.error('Please select an avatar first');
      return;
    }
    
    if (trialStatus.messages_remaining <= 0) {
      toast.error('Free trial messages exhausted. Please buy a pass!');
      return;
    }
    
    const userMessage = {
      role: 'user',
      content: content,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setSending(true);
    
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/trial/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trial_id: trialId,
          avatar_id: selectedAvatar.id,
          message: content
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, assistantMessage]);
        setTrialStatus(prev => ({
          ...prev,
          messages_used: (prev.messages_used || 0) + 1,
          messages_remaining: Math.max(0, (prev.messages_remaining || 0) - 1)
        }));
      } else {
        toast.error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Error sending message');
    }
    setSending(false);
  };
  
  const handleVoiceResult = (transcript) => {
    if (transcript) {
      sendTrialMessage(transcript);
    }
    setShowVoiceInput(false);
  };
  
  // Avatar selection view
  if (!selectedAvatar) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={onBack} className="text-white" data-testid="trial-back-btn">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Free Trial</h1>
            <p className="text-gray-400 text-sm">Try our AI consultation for free!</p>
          </div>
        </div>
        
        {/* Trial Status */}
        <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl p-4 mb-6 border border-green-500/30 max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-8 h-8 text-green-400" />
              <div>
                <p className="font-bold text-white">Free Messages</p>
                <p className="text-gray-400 text-sm">No login required</p>
              </div>
            </div>
            <div className="text-4xl font-black text-green-400">
              {trialStatus?.messages_remaining || 3}
            </div>
          </div>
        </div>
        
        {/* Avatar Selection */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-lg font-bold text-white mb-4">Select an Avatar to Chat With</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {avatars.slice(0, 8).map(avatar => (
              <div
                key={avatar.id}
                onClick={() => setSelectedAvatar(avatar)}
                className="bg-slate-800/50 rounded-xl p-4 cursor-pointer hover:bg-slate-700/50 transition-all border border-slate-700 hover:border-purple-500"
                data-testid={`trial-avatar-${avatar.id}`}
              >
                <div className="w-16 h-16 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-pink-600 mb-3">
                  {avatar.image_url ? (
                    <img src={avatar.image_url} alt={avatar.display_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Bot className="w-8 h-8 text-white" />
                    </div>
                  )}
                </div>
                <h3 className="text-white font-bold text-center text-sm">{avatar.display_name}</h3>
                <p className="text-gray-400 text-xs text-center capitalize">{avatar.category}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Chat view
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex flex-col">
      {/* Header */}
      <div className="bg-slate-800/80 backdrop-blur-sm p-4 flex items-center justify-between border-b border-slate-700">
        <div className="flex items-center gap-3">
          <button onClick={() => setSelectedAvatar(null)} className="text-white">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <TrialAvatar avatar={selectedAvatar} />
          <div>
            <h2 className="text-white font-bold">{selectedAvatar.display_name}</h2>
            <p className="text-green-400 text-xs flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Free Trial
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-green-500/20 px-3 py-1 rounded-full">
            <span className="text-green-400 text-sm font-bold">
              {trialStatus?.messages_remaining || 0} messages left
            </span>
          </div>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Welcome Message */}
        {messages.length === 0 && (
          <div className="text-center py-8">
            <TrialAvatar avatar={selectedAvatar} />
            <h3 className="text-white font-bold mt-4">
              Namaste! I am {selectedAvatar.display_name}
            </h3>
            <p className="text-gray-400 mt-2 text-sm">
              You have {trialStatus?.messages_remaining || 3} free messages. Ask me anything!
            </p>
          </div>
        )}
        
        {messages.map((msg, idx) => (
          <TrialMessage key={idx} message={msg} isUser={msg.role === 'user'} />
        ))}
        
        {sending && (
          <div className="flex gap-3">
            <div className="bg-slate-700/50 rounded-2xl px-4 py-3">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Trial Exhausted Banner */}
      {trialStatus?.messages_remaining <= 0 && (
        <div className="bg-amber-500/20 border-t border-amber-500/30 p-4">
          <div className="max-w-md mx-auto text-center">
            <p className="text-amber-400 font-bold mb-2">Free trial exhausted!</p>
            <button
              onClick={onBuyPass}
              className="px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-xl hover:opacity-90"
              data-testid="buy-pass-after-trial-btn"
            >
              Buy a Pass to Continue
            </button>
          </div>
        </div>
      )}
      
      {/* Voice Input Modal */}
      {showVoiceInput && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-bold">Voice Input</h3>
              <button onClick={() => setShowVoiceInput(false)} className="text-gray-400">
                <X className="w-6 h-6" />
              </button>
            </div>
            <VoiceRecorder onResult={handleVoiceResult} />
          </div>
        </div>
      )}
      
      {/* Input Area */}
      {trialStatus?.messages_remaining > 0 && (
        <div className="bg-slate-800/80 backdrop-blur-sm p-4 border-t border-slate-700">
          <div className="flex items-center gap-2 max-w-4xl mx-auto">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendTrialMessage(inputMessage)}
              placeholder="Type your message..."
              className="flex-1 px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-purple-500 focus:outline-none"
              data-testid="trial-message-input"
            />
            <button
              onClick={() => sendTrialMessage(inputMessage)}
              disabled={sending || !inputMessage.trim()}
              className="p-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl text-white hover:opacity-90 disabled:opacity-50"
              data-testid="trial-send-btn"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreeTrialView;
